import {
  DEFAULT_CERTIFICATE_TEMPLATE_ID,
  type CertificateTemplateId,
} from '../types/certificateTemplate';
import {
  COURSE_CERTIFICATE_VALUE_TO_TEMPLATE_ID,
  normalizeCourseCertificateValue,
} from './courseCertificateTemplateByCourseId';
import becomeLogo from './logos/become.png';
import irisLogo from './logos/iris.png';
import oplLogo from './logos/opl.png';
import rinaLogo from './logos/rina.png';
import francescasSig from './signatures/francescas.png';
import gabrielegSig from './signatures/gabrieleg.png';
import lucabSig from './signatures/lucab.png';

/**
 * Firme disponibili per i certificati (immagini in certificateTemplates/signatures).
 */
export const SIGNATURE_IDS = [
  'lucaBernardelli',
  'francescaSforza',
  'gabrieleGramaglia',
] as const;

export type SignatureId = (typeof SIGNATURE_IDS)[number];

type SignatureDefinition = {
  nameLine: string;
  roleLine: string;
  imageSrc: string;
};

const SIGNATURE_DEFINITIONS: Record<SignatureId, SignatureDefinition> = {
  lucaBernardelli: {
    nameLine: 'dott. Luca Bernardelli',
    roleLine: 'Responsabile Scientifico',
    imageSrc: lucabSig,
  },
  francescaSforza: {
    nameLine: 'dott.ssa Francesca Sforza',
    roleLine: 'Coordinamento didattico',
    imageSrc: francescasSig,
  },
  gabrieleGramaglia: {
    nameLine: 'dott. Gabriele Gramaglia',
    roleLine: 'Responsabile Didattico',
    imageSrc: gabrielegSig,
  },
};

/** Logo BECOME (basso a sinistra sul certificato) */
export const BECOME_LOGO_SRC: string = becomeLogo;

/** Logo Iris (basso a destra, opzionale per corso) */
export const IRIS_LOGO_SRC: string = irisLogo;

/** Logo Ordine Psicologi Lombardia (attestazione ECM / cert2) */
export const OPL_LOGO_SRC: string = oplLogo;

/** Logo RINA / certificazione qualità (attestazione ECM / cert2) */
export const RINA_LOGO_SRC: string = rinaLogo;

export type CourseCertificationConfig = {
  /** Ordine delle colonne firma (sottoinsieme di SIGNATURE_IDS) */
  signatures: SignatureId[];
  /** Mostra il logo Iris in basso a destra */
  showIrisLogo: boolean;
};

/**
 * Default: Luca + Gabriele; logo Iris attivo salvo override per corso.
 * Aggiungi qui gli id corso dall’API per personalizzare.
 */
export const DEFAULT_COURSE_CERTIFICATION_CONFIG: CourseCertificationConfig = {
  signatures: ['lucaBernardelli', 'gabrieleGramaglia'],
  showIrisLogo: true,
};

/**
 * Configurazione per id corso (chiave = course.id dall’API).
 *
 * Esempio:
 *   8: {
 *     signatures: ['lucaBernardelli', 'francescaSforza', 'gabrieleGramaglia'],
 *     showIrisLogo: true,
 *   },
 */
export const COURSE_CERTIFICATION_BY_COURSE_ID: Partial<
  Record<number, CourseCertificationConfig>
> = {};

export function getCourseCertificationConfig(
  courseId: number
): CourseCertificationConfig {
  const override = COURSE_CERTIFICATION_BY_COURSE_ID[courseId];
  if (!override) {
    return DEFAULT_COURSE_CERTIFICATION_CONFIG;
  }
  return {
    ...DEFAULT_COURSE_CERTIFICATION_CONFIG,
    ...override,
  };
}

export function getCertificateTemplateId(
  courseCertificate: string | null | undefined
): CertificateTemplateId {
  const certValue = normalizeCourseCertificateValue(courseCertificate);
  return (
    COURSE_CERTIFICATE_VALUE_TO_TEMPLATE_ID[certValue] ??
    DEFAULT_CERTIFICATE_TEMPLATE_ID
  );
}

export type CertificateSignatureSlot = {
  id: SignatureId;
  nameLine: string;
  roleLine: string;
  imageSrc: string;
};

/**
 * Dati risolti per il template certificato (firme + logo Iris).
 */
export function resolveCertificateBranding(
  courseId: number,
  courseCertificate: string | null | undefined = null
): {
  signatureSlots: CertificateSignatureSlot[];
  showIrisLogo: boolean;
} {
  const cfg = getCourseCertificationConfig(courseId);
  const certValue = normalizeCourseCertificateValue(courseCertificate);
  const signatureIds =
    certValue === 'cert1'
      ? (['lucaBernardelli', 'francescaSforza', 'gabrieleGramaglia'] as const)
      : cfg.signatures;

  const signatureSlots: CertificateSignatureSlot[] = signatureIds.map(id => {
    const def = SIGNATURE_DEFINITIONS[id];
    return {
      id,
      nameLine: def.nameLine,
      roleLine: def.roleLine,
      imageSrc: def.imageSrc,
    };
  });
  return {
    signatureSlots,
    showIrisLogo: cfg.showIrisLogo,
  };
}
